import {
  Container,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography
} from '@mui/material'

import ArrowRightIcon from '@mui/icons-material/ArrowRight'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CodeSnippet from 'src/components/CodeSnippet'
import { FunctionComponent } from 'react'

const MigrationGuide: FunctionComponent = () => {
  return (
    <Container maxWidth="md" sx={{ p: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          Migration Guide: Upgrading from API v1 to API v2
        </Typography>

        <Typography variant="body1">
          API v2 introduces a more secure and flexible authentication approach, moving from Basic
          Authentication to OAuth 2.0. Replacing previously required user-based authentication, API
          V2 introduces Machine-to-Machine (M2M) tokens for seamless service integrations.
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          Key Changes
        </Typography>
        <List dense>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="Authentication: Basic Authentication replaced by OAuth 2.0 for enhanced security." />
          </ListItem>
          <ListItem>
            <ListItemIcon>
              <CheckCircleIcon color="primary" />
            </ListItemIcon>
            <ListItemText primary="M2M Integrations: New M2M tokens for service-to-service authentication, allowing automated workflows without user interaction." />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          Step-by-Step Migrating to M2M Authentication Instructions
        </Typography>

        <List dense>
          <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
            <ListItemText
              primary="1. Access the Developer Console Dashboard"
              secondary="Users can generate and manage Machine-to-Machine (M2M) API tokens by logging into their tenant’s application and navigating to the Developer Console Dashboard."
            />
            <List dense component="div" disablePadding>
              <ListItem>
                <ListItemIcon>
                  <ArrowRightIcon />
                </ListItemIcon>
                <ListItemText primary="Log in and go to Developer Console > Workspace > API Tokens." />
              </ListItem>
            </List>
          </ListItem>

          <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
            <ListItemText primary="2. Create an M2M API Token" />
            <List dense component="div" disablePadding>
              <ListItem>
                <ListItemIcon>
                  <ArrowRightIcon />
                </ListItemIcon>
                <ListItemText primary="Navigate to the Developer Console Dashboard by selecting Developer Console > Workspace > API Tokens from the NavBar’s top-right menu" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <ArrowRightIcon />
                </ListItemIcon>
                <ListItemText primary="Click Add Token and enter a description." />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <ArrowRightIcon />
                </ListItemIcon>
                <ListItemText primary="Assign a role prefixed with 'M2M' to the token (required for API authentication)." />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <ArrowRightIcon />
                </ListItemIcon>
                <ListItemText primary="Optionally, set an expiration date for the token; it defaults to 'never.'" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <ArrowRightIcon />
                </ListItemIcon>
                <ListItemText primary="After setup, click Save to generate the token." />
              </ListItem>
            </List>
          </ListItem>

          <ListItem>
            <ListItemText
              primary="3. Save Token Credentials"
              secondary="Copy and store the Client ID and Secret Key as they won’t be shown again."
            />
          </ListItem>

          <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
            <ListItemText
              primary="4. Exchange for an Access Token"
              secondary="Use the Client ID and Secret Key to request an access token. Send these values as parameters in the POST body to authenticate against the Angaza authentication domain."
            />
            <CodeSnippet
              snippet={`curl --request POST \\
       --url https://auth.angaza-dev.net/identity/resources/auth/v2/api-token/ \\
       --header 'accept: application/json' \\
       --header 'content-type: application/json' \\
       --data '{
          "clientId": "<client_id>",
          "secret": "<secret>"
       }'`}
            />
            <Typography variant="body2">
              Copy the access token from the response for API requests.
            </Typography>
            <Typography variant="body2">
              Note: Each token is limited to 100 simultaneous refresh tokens. Upon the 101st
              refresh, the oldest token is automatically invalidated.
            </Typography>
          </ListItem>

          <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
            <ListItemText
              primary="5. Update Request Headers"
              secondary="Use Bearer tokens in the Authorization header, and add X-API-HEADER with the API key."
            />
            <List dense component="div" disablePadding>
              <ListItem>
                <ListItemIcon>
                  <ArrowRightIcon />
                </ListItemIcon>
                <ListItemText primary="In your API requests, replace Authorization: Basic <base64encodedCredentials> with Authorization: Bearer <access_token>" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <ArrowRightIcon />
                </ListItemIcon>
                <ListItemText primary="Copy the API Key from the top-right menu and include it as the X-API-HEADER in your request headers." />
              </ListItem>
            </List>
            <CodeSnippet
              snippet={`curl --request GET \\
       --url https://example-api.com/data \\
       --header 'Authorization: Bearer <access_token>' \\
       --header 'X-API-KEY: <tenant-api-key>' \\`}
            />
          </ListItem>

          <ListItem sx={{ flexDirection: 'column', alignItems: 'flex-start' }}>
            <ListItemText
              primary="6. Error Handling & Token Refresh"
              secondary="Update error-handling logic to manage token expiration and handle 401 and 403 responses. Refresh tokens as needed by using the following endpoint if the access token expires."
            />
            <CodeSnippet
              snippet={`curl --request POST \\
       --url https://auth.angaza-dev.net/identity/resources/auth/v2/api-token/token/refresh \\
       --header 'accept: application/json' \\
       --header 'content-type: application/json' \\
       --header 'Authorization: Bearer <access_token>' \
       --data '{
          "refreshToken": "<refresh_token>"
       }'`}
            />
          </ListItem>
        </List>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          Additional Notes
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary="API Endpoint Changes: Review the API v2 documentation for any changes in endpoint structures or request parameters." />
          </ListItem>
          <ListItem>
            <ListItemText primary="Testing: Test the new OAuth and M2M flows in a staging environment before going live." />
          </ListItem>
        </List>
      </Paper>
    </Container>
  )
}

export default MigrationGuide
